import { Component } from '@theme/component';
import { preventDefault } from '@theme/utilities';

/**
 * Roster strip + profile list for the Rabbit Teams page. Tapping a thumbnail
 * in the horizontal scroll strip (role="tab") shows that athlete's profile
 * card (role="tabpanel") below it and hides the rest, one at a time. The
 * strip also supports mouse click-and-drag scrolling on desktop (native
 * `overflow-x: auto` only responds to touch/trackpad/scrollbar, not a mouse
 * drag) - the drag handling here mirrors `assets/slideshow.js`'s
 * `#handleMouseDown` pattern, minus its slide-snapping, since this is a
 * free-scrolling strip rather than a slide-by-slide carousel.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} tab - The roster thumbnail buttons.
 * @property {HTMLElement[]} panel - The profile card panels.
 * @property {HTMLElement} row - The horizontal scroll strip.
 * @property {HTMLElement} [swipeControls] - Wrapper around the swipe buttons, hidden when nothing overflows.
 * @property {HTMLButtonElement} [swipePrev] - Optional "← Swipe" button that scrolls the strip back.
 * @property {HTMLButtonElement} [swipeNext] - Optional "Swipe →" button that scrolls the strip forward.
 *
 * @extends {Component<Refs>}
 */
class TeamRosterTabs extends Component {
  requiredRefs = ['tab', 'panel', 'row'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.refs.row.addEventListener('mousedown', this.#handleMouseDown);

    // The swipe buttons only make sense while the row actually overflows - once
    // every thumbnail fits (and the row is centered), there's nothing to scroll.
    this.#resizeObserver = new ResizeObserver(this.#updateSwipeControls);
    this.#resizeObserver.observe(this.refs.row);
    this.refs.row.addEventListener('scroll', this.#updateSwipeControls, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.refs.row.removeEventListener('mousedown', this.#handleMouseDown);
    this.refs.row.removeEventListener('scroll', this.#updateSwipeControls);
    this.#resizeObserver?.disconnect();
  }

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /**
   * Hides the swipe buttons when the row doesn't overflow, and disables whichever
   * one points past the start/end of the row.
   */
  #updateSwipeControls = () => {
    const { row, swipeControls, swipePrev, swipeNext } = this.refs;
    if (!swipeControls) return;

    const maxScroll = row.scrollWidth - row.clientWidth;
    swipeControls.hidden = maxScroll <= 1;
    if (swipePrev) swipePrev.disabled = row.scrollLeft <= 1;
    if (swipeNext) swipeNext.disabled = row.scrollLeft >= maxScroll - 1;
  };

  /** Scrolls the row back by most of its visible width. Triggered via `on:click`. */
  scrollPrev() {
    this.#scrollByPage(-1);
  }

  /** Scrolls the row forward by most of its visible width. Triggered via `on:click`. */
  scrollNext() {
    this.#scrollByPage(1);
  }

  /** @param {1 | -1} direction */
  #scrollByPage(direction) {
    const row = this.refs.row;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    row.scrollBy({ left: direction * row.clientWidth * 0.8, behavior });
  }

  /**
   * Activate the tab that was tapped/clicked. `on:click` is dispatched through a single
   * document-level listener (see `@theme/component`), so `event.currentTarget` is always
   * `document` here, not the button - the actual tab has to be found from `event.target`.
   * @param {Event} event
   */
  activate(event) {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    if (!(tab instanceof HTMLElement)) return;

    this.#activateTab(tab);
  }

  /**
   * Move focus and activation between tabs with the arrow/Home/End keys, per the WAI-ARIA
   * tabs pattern's "automatic activation" model.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    const tabs = this.refs.tab;
    const currentIndex = tabs.findIndex((tab) => tab === document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    if (!nextTab) return;

    nextTab.focus();
    this.#activateTab(nextTab);
  };

  /**
   * @param {HTMLElement} tab
   */
  #activateTab(tab) {
    const targetId = tab.getAttribute('aria-controls');

    this.refs.tab.forEach((otherTab) => {
      const selected = otherTab === tab;
      otherTab.setAttribute('aria-selected', String(selected));
      otherTab.tabIndex = selected ? 0 : -1;
    });

    /** @type {HTMLElement | undefined} */
    let targetPanel;

    this.refs.panel.forEach((panel) => {
      const active = panel.id === targetId;
      // Mobile shows one profile at a time, so `hidden` drives visibility there.
      // Desktop shows every profile at once and overrides `[hidden]` back to
      // visible in CSS - the `--active` class is what highlights the match there.
      panel.hidden = !active;
      panel.classList.toggle('bst-team-roster__profile--active', active);
      if (active) targetPanel = panel;
    });

    tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    targetPanel?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  #dragging = false;

  /**
   * Starts a mouse click-and-drag scroll of the roster row. Only listens for `mousedown`
   * (not `pointerdown`), matching `assets/slideshow.js` - touch already scrolls the row
   * natively, so this should only ever intercept real mouse drags.
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    if (this.#dragging) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('a, [contenteditable]')) return;

    // Prevents native text/image drag-out (the row's thumbnails are <img> elements)
    // while still letting a plain click through - only a real drag suppresses the
    // trailing `click` below, so tapping a tab to select it still works.
    event.preventDefault();

    const row = this.refs.row;
    const startPosition = event.x;
    const controller = new AbortController();
    const { signal } = controller;
    let previous = startPosition;
    let moved = false;

    this.#dragging = true;

    /** @param {PointerEvent} event */
    const onPointerMove = (event) => {
      const current = event.x;
      const delta = previous - current;
      if (!delta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);
        document.addEventListener('click', preventDefault, { once: true, signal });
        row.style.scrollSnapType = 'none';
        row.setAttribute('dragging', '');
      }

      previous = current;
      row.scrollBy({ left: delta, behavior: 'instant' });
    };

    /** @param {PointerEvent} event */
    const onPointerUp = (event) => {
      controller.abort();
      this.#dragging = false;
      row.style.scrollSnapType = '';
      row.removeAttribute('dragging');

      if (moved) this.releasePointerCapture(event.pointerId);
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    // pointerdown also ends the drag, matching slideshow.js - guards against a stray
    // second pointerdown (e.g. a second mouse button) leaving the row stuck mid-drag.
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };
}

if (!customElements.get('team-roster-tabs')) {
  customElements.define('team-roster-tabs', TeamRosterTabs);
}
